import { Link, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  useGetAssessorDetailsQuery,
  useUpdateAssessorMutation,
} from "../api/assessorsApi";

const fullName = (m: any) =>
  [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—";

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const HISTORY_PAGE_SIZE = 10;

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: any }) {
  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b bg-indigo-50/40">
        <div className="font-semibold text-gray-900">{title}</div>
        {subtitle ? <div className="text-sm text-gray-500 mt-0.5">{subtitle}</div> : null}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Stat({ label, value, tint }: { label: string; value: any; tint: string }) {
  return (
    <div className={`rounded-xl border bg-gradient-to-b p-4 ${tint}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-3xl font-extrabold mt-1 text-gray-900">{value}</div>
    </div>
  );
}

const Row = ({ label, value }: { label: string; value: any }) => (
  <div className="flex justify-between gap-4 border-b last:border-0 py-2">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium break-all text-right">{value || "—"}</span>
  </div>
);

export default function AssessorDetailsPage() {
  const { id } = useParams();
  const assessorId = String(id || "");

  const { data: assessor, isLoading, error, refetch } = useGetAssessorDetailsQuery(assessorId, {
    skip: !assessorId,
  });

  const member = useMemo(() => (assessor as any)?.member, [assessor]);
  // The assessor's assessment history (logbooks they have graded), newest first.
  const graded = useMemo(() => {
    const list = ((assessor as any)?.assessed_logbooks || []) as any[];
    return [...list].sort(
      (a, b) =>
        new Date(b?.graded_at || 0).getTime() - new Date(a?.graded_at || 0).getTime()
    );
  }, [assessor]);

  const [historyPage, setHistoryPage] = useState(1);
  const historyPages = Math.max(1, Math.ceil(graded.length / HISTORY_PAGE_SIZE));
  const pagedGraded = useMemo(
    () => graded.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE),
    [graded, historyPage]
  );

  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Update assessor settings
  const [editing, setEditing] = useState(false);
  const [active, setActive] = useState<boolean>(true);
  const [maxWorkload, setMaxWorkload] = useState<string>("10");
  const [updateAssessor, { isLoading: savingAssessor }] = useUpdateAssessorMutation();

  // initialize edit fields when assessor loads
  useMemo(() => {
    if (!assessor) return;
    setActive(Boolean((assessor as any).active));
    setMaxWorkload(String((assessor as any).max_workload ?? 10));
  }, [assessor]);

  if (isLoading) return <div className="text-sm text-gray-600 p-6">Loading…</div>;
  if (error) return <div className="text-sm text-red-600 p-6">Failed to load</div>;
  if (!assessor) return <div className="text-sm text-gray-600 p-6">Not found</div>;

  const onSaveAssessor = async () => {
    setMsg(null);
    const mw = Number(maxWorkload);
    if (!mw || Number.isNaN(mw) || mw < 1) {
      setMsg({ type: "error", text: "Max workload must be a valid number >= 1" });
      return;
    }
    try {
      const res = await updateAssessor({ id: assessorId, active, max_workload: mw }).unwrap();
      if (res?.status && res.status !== "success") {
        throw { data: { message: res?.message || "Update failed" } };
      }
      setMsg({ type: "success", text: res?.message || "Assessor updated" });
      setEditing(false);
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Update failed" });
    }
  };

  const avgScore = (() => {
    const nums = graded.map((x) => Number(x.total_score)).filter((n) => !Number.isNaN(n));
    return nums.length ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2) : "—";
  })();

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">{fullName(member)}</h1>
            <p className="text-sm text-white/80 mt-1">
              {(assessor as any).active ? "Active assessor" : "Inactive"} • Max workload:{" "}
              {(assessor as any).max_workload ?? "—"}
            </p>
            <p className="text-xs text-white/70 mt-1 break-all">Assessor ID: {(assessor as any).id}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/assessors" className="px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm">
              Back
            </Link>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {msg && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            msg.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Graded Logbooks" value={graded.length} tint="from-indigo-50 to-white border-indigo-100 text-indigo-700" />
        <Stat label="Average Score" value={avgScore} tint="from-purple-50 to-white border-purple-100 text-purple-700" />
        <Stat
          label="Status"
          value={(assessor as any).active ? "Active" : "Inactive"}
          tint="from-emerald-50 to-white border-emerald-100 text-emerald-700"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Member info */}
        <Section title="Member Info">
          <div className="text-sm text-gray-700">
            <Row label="Member Name" value={`${member?.surname ?? ""} ${member?.firstname ?? ""}`.trim()} />
            <Row label="Membership No" value={member?.membership_no} />
            <Row label="Email" value={member?.email} />
            <Row label="Phone" value={member?.phone} />
            <Row label="Phone 2" value={member?.phone2} />
            <Row label="State" value={member?.state} />
            <div className="pt-3">
              <div className="text-xs text-gray-500">Address</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{member?.address || "—"}</div>
            </div>
          </div>
        </Section>

        {/* Assessor settings */}
        <Section title="Assessor Settings">
          {!editing ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-700">
                <Row label="Active" value={(assessor as any).active ? "Yes" : "No"} />
                <Row label="Max Workload" value={(assessor as any).max_workload ?? "—"} />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm"
                >
                  Edit Settings
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm text-gray-600">Active</label>
                <select
                  value={active ? "1" : "0"}
                  onChange={(e) => setActive(e.target.value === "1")}
                  className="border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm text-gray-600">Max Workload</label>
                <input
                  value={maxWorkload}
                  onChange={(e) => setMaxWorkload(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                  inputMode="numeric"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  disabled={savingAssessor}
                  onClick={() => {
                    setEditing(false);
                    setActive(Boolean((assessor as any).active));
                    setMaxWorkload(String((assessor as any).max_workload ?? 10));
                  }}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  disabled={savingAssessor}
                  onClick={onSaveAssessor}
                  className="px-4 py-2 rounded-lg bg-gradient-purple text-white hover:opacity-90 text-sm disabled:opacity-60"
                >
                  {savingAssessor ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Assessment history */}
      <Section
        title="Assessment History"
        subtitle="Logbooks this assessor has graded. Assessments are run from the Logbook Assessment diets."
      >
        {graded.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 font-semibold">Candidate</th>
                    <th className="py-2 font-semibold">Diet</th>
                    <th className="py-2 font-semibold">Stage</th>
                    <th className="py-2 font-semibold">Status</th>
                    <th className="py-2 font-semibold">Score</th>
                    <th className="py-2 font-semibold">Graded</th>
                    <th className="py-2 font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedGraded.map((l: any) => (
                    <tr key={l.id} className="border-b last:border-0 hover:bg-gray-50/60">
                      <td className="py-3">
                        <Link
                          to={`/admin/logbooks/${l.id}`}
                          className="font-medium text-gray-900 hover:text-indigo-600 hover:underline"
                        >
                          {l.user?.name ?? "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{l.user?.email ?? ""}</div>
                      </td>
                      <td className="py-3 text-gray-700">{l.diet?.title ?? "—"}</td>
                      <td className="py-3">Stage {l.stage ?? "—"}</td>
                      <td className="py-3">
                        <span className="inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                          {l.status}
                        </span>
                      </td>
                      <td className="py-3 font-semibold">
                        {l.total_score != null ? `${l.total_score}/100` : "—"}
                      </td>
                      <td className="py-3 text-gray-500">{fmtDate(l.graded_at)}</td>
                      <td className="py-3 text-gray-600 max-w-[220px] truncate" title={l.remarks ?? ""}>
                        {l.remarks ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {historyPages > 1 && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-gray-500">
                  Page {historyPage} of {historyPages} · {graded.length} total
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={historyPage <= 1}
                    className="px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setHistoryPage((p) => Math.min(historyPages, p + 1))}
                    disabled={historyPage >= historyPages}
                    className="px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-gray-500">No graded logbooks yet.</div>
        )}
      </Section>
    </div>
  );
}
